import { datadogLog } from '../../../lib/analytics'
import { type ApiError, apiFetch } from '../../../lib/api/client'
import { useBookingStore } from '../../../store'
import type {
  CreateBookingResponse,
  CustomerResponse,
  UserInfo,
} from '../UserData.types'
import { buildCreateBookingPayload, toPrefilledUser } from '../UserData.utils'

const SLOT_UNAVAILABLE_ERROR_CODE = 'no_available_spots'

/**
 * True when create-booking was rejected because the slot filled up between
 * selection and submit (409 + `no_available_spots`). Branches on `errorCode`,
 * not the free-text `detail`, which Waitwhile can change.
 */
export const isSlotUnavailableError = (error: unknown): boolean => {
  const { status, errorCode } = (error ?? {}) as Partial<ApiError>
  return status === 409 && errorCode === SLOT_UNAVAILABLE_ERROR_CODE
}

/**
 * Creates the appointment and returns the new booking id.
 * Reads store / employee / timeslot context straight from the booking store,
 * matching the other API modules in this package.
 */
export async function createBooking(user: UserInfo): Promise<string> {
  const payload = buildCreateBookingPayload(useBookingStore.getState(), user)

  const { id } = await apiFetch<CreateBookingResponse>('v2/booking/create', {
    method: 'POST',
    body: JSON.stringify(payload),
  })

  return id
}

/**
 * Fetches the signed-in customer's details.
 *
 * The only authorized call in this package: `signature` goes out as the
 * `authorization` header, exactly as the legacy tool sent it.
 */
export async function getCustomer(
  userId: string,
  signature: string,
): Promise<CustomerResponse> {
  return apiFetch<CustomerResponse>(`customer/customer/${userId}`, {
    headers: { authorization: signature },
  })
}

/**
 * Prefills the user-data form for a known customer.
 *
 * Requires both a `userId` and a `signature` — the host has to have signed the
 * customer in; an id on its own is not enough to read their details. Skipped
 * once the form holds anything, so it never overwrites what the user typed or
 * repeats itself on a second open.
 *
 * Best-effort by design: a failure leaves the form empty for the user to fill
 * in, so the booking flow never depends on it. Mirrors the legacy
 * `attemptUpdateCustomer`.
 */
export async function prefillUserData(): Promise<void> {
  const { userId, signature, userData, isPrefilled, setState } =
    useBookingStore.getState()

  if (!userId || !signature || isPrefilled || userData?.email) return

  try {
    const customer = await getCustomer(userId, signature)
    setState({ userData: toPrefilledUser(customer), isPrefilled: true })
    datadogLog('bookingToolUserDataPrefilled', { userId })
  } catch {
    // Non-blocking: the user fills the form in by hand.
  }
}
